/**
 * Resolve where an event's map should open BEFORE the map is constructed.
 *
 * The old flow built the map at a hardcoded fallback city and then flew to
 * the geocoded venue — a jarring cross-continent race on every visit. Pages
 * now wait for this resolution (boundary/saved center are synchronous;
 * geocoding takes one short request, cached per address so revisits are
 * instant) and construct the map directly at the right spot.
 */
export type LngLat = [number, number]

/** Last-resort center when there is no boundary, no saved center, and the
 * venue address is missing or fails to geocode. */
export const FALLBACK_MAP_CENTER: LngLat = [-73.9857, 40.7484]

const cacheKey = (venueAddress: string) => `venue-center:${venueAddress}`

export function cachedVenueCenter(venueAddress?: string): LngLat | null {
  if (!venueAddress) return null
  try {
    const raw = localStorage.getItem(cacheKey(venueAddress))
    if (!raw) return null
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) && parsed.length === 2 ? (parsed as LngLat) : null
  } catch {
    return null
  }
}

export async function geocodeVenueCenter(
  venueAddress: string,
  mapboxToken: string,
): Promise<LngLat | null> {
  try {
    const res = await fetch(
      `https://api.mapbox.com/geocoding/v5/mapbox.places/${encodeURIComponent(venueAddress)}.json?access_token=${mapboxToken}&limit=1`,
    )
    const data = await res.json()
    const center = data?.features?.[0]?.center
    if (Array.isArray(center) && center.length === 2) {
      try {
        localStorage.setItem(cacheKey(venueAddress), JSON.stringify(center))
      } catch {
        // storage unavailable — still return the result
      }
      return center as LngLat
    }
  } catch {
    // network failure — caller falls back
  }
  return null
}
